import { consumeOnboardingPending } from '~/utils/msalOAuthSession'

const LOGIN_OR_REGISTER: RegExp = /^\/(login|register)(\/|\?|#|$)/i

const DEFAULT_LOGGED_IN_PATH: string = '/discuter'

const ONBOARDING_PATH: string = '/onboarding'

const LOGIN_REDIRECT_STORAGE_KEY: string = 'prepeers:login-redirect'

const REDIRECT_SANITIZE_MISS: string = '\x00no-redirect\x00'

/**
 * Enregistre le chemin `redirect` (query) pour le retrouver après le retour OAuth
 * (MSAL ne conserve pas toujours les query sur `/login`).
 * @param {string | string[] | undefined | null} redirect Valeur de `route.query.redirect`.
 * @returns {void}
 */
export function persistLoginRedirectFromRouteQuery(
  redirect: string | string[] | undefined | null,
): void {
  if (!import.meta.client) return
  const safe: string = sanitizeInternalRedirectPath(redirect, REDIRECT_SANITIZE_MISS)
  if (safe === REDIRECT_SANITIZE_MISS) return
  sessionStorage.setItem(LOGIN_REDIRECT_STORAGE_KEY, safe)
}

/**
 * @returns {string | null} Valeur stockée pour post-login, ou `null`.
 */
export function peekStoredLoginRedirect(): string | null {
  if (!import.meta.client) return null
  return sessionStorage.getItem(LOGIN_REDIRECT_STORAGE_KEY)
}

/**
 * Supprime la cible post-login stockée (appeler après navigation réussie ou abandon).
 * @returns {void}
 */
export function clearStoredLoginRedirect(): void {
  if (!import.meta.client) return
  sessionStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY)
}

/**
 * Filtre une valeur de query `redirect` : chemins internes uniquement, sans open redirect.
 * @param {string | string[] | undefined | null} raw Valeur brute (string, tableau Vue Router, null/undefined).
 * @param {string} fallback Chemin utilisé si la valeur est absente ou invalide.
 * @returns {string} Un chemin relatif sûr (inchangé si valide après décodage).
 */
export function sanitizeInternalRedirectPath(
  raw: string | string[] | undefined | null,
  fallback: string,
): string {
  const first: string | undefined = Array.isArray(raw) ? raw[0] : (raw ?? undefined)
  if (typeof first !== 'string' || !first.trim()) return fallback
  let decoded: string
  try {
    decoded = decodeURIComponent(first.trim())
  } catch {
    return fallback
  }
  const beforeHash: string = decoded.split('#')[0] ?? ''
  if (!beforeHash.startsWith('/') || beforeHash.startsWith('//')) return fallback
  if (beforeHash.includes('://')) return fallback
  if (LOGIN_OR_REGISTER.test(beforeHash)) return fallback
  return decoded
}

/**
 * Retourne un chemin `redirect` sûr pour l'app, ou `null` si aucune cible valide.
 * Même priorité que le post-login : stockage session puis query route.
 * @param {string | null} storedRedirect - Valeur `peekStoredLoginRedirect()` ou équivalent.
 * @param {string | string[] | undefined | null} queryRedirect - Valeur `route.query.redirect`.
 * @returns {string | null} Chemin relatif interne ou `null`.
 */
export function getSanitizedLoginRedirectOrNull(
  storedRedirect: string | null,
  queryRedirect: string | string[] | undefined | null,
): string | null {
  const raw: string | string[] | undefined | null = storedRedirect ?? queryRedirect ?? undefined
  const safe: string = sanitizeInternalRedirectPath(raw, REDIRECT_SANITIZE_MISS)
  if (safe === REDIRECT_SANITIZE_MISS) return null
  return safe
}

/**
 * Choisit la route après connexion : l'onboarding si le compte n'a pas encore répondu aux questions du tunnel, sinon la cible demandée si valide, sinon `/discuter` par défaut.
 * @param {string} preferredPath Chemin souhaité (déjà sanitizé ou défaut applicatif).
 * @returns {Promise<string>} Route Nuxt finale (`/onboarding`, `preferredPath` ou `/discuter`).
 */
export async function resolvePostLoginRoute(preferredPath: string): Promise<string> {
  if (consumeOnboardingPending()) return ONBOARDING_PATH

  return preferredPath || DEFAULT_LOGGED_IN_PATH
}

export { DEFAULT_LOGGED_IN_PATH }
